import React, { useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { DeleteOutlined, EditOutlined, VisibilityOutlined } from '@mui/icons-material';
import { useGetRolesHandler } from '../dataHandlers/getRolesHandler';
import { useDeleteRoleHandler } from '../dataHandlers/deleteRoleHandler';
import RoleFormModal from './RoleFormModal';
import RoleDeleteDialog from './RoleDeleteDialog';

const paperSx = {
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'divider',
  borderRadius: 2,
  overflow: 'hidden',
};

const headerCellSx = {
  color: 'text.secondary',
  fontSize: '0.75rem',
  fontWeight: 500,
  borderBottom: 1,
  borderColor: 'divider',
  py: 1,
};

const bodyCellSx = {
  borderBottom: 1,
  borderColor: 'divider',
  py: 1,
};

export default function RolesTable({ searchQuery = {}, onPaginationChange }) {
  const { roles, totalElements, isLoading } = useGetRolesHandler({ searchQuery });

  const [modalMode, setModalMode] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { mutate: deleteRole, isPending: isDeleting } = useDeleteRoleHandler({
    onSuccess: () => {
      setDeleteOpen(false);
      setSelectedRole(null);
    },
  });

  const openModal = (mode, role) => {
    setSelectedRole(role);
    setModalMode(mode);
  };

  const closeModal = () => {
    setModalMode(null);
    setSelectedRole(null);
  };

  const handleChangePage = (_, newPage) => {
    onPaginationChange(newPage, searchQuery.size ?? 10);
  };

  const handleChangeRowsPerPage = (event) => {
    onPaginationChange(0, parseInt(event.target.value, 10));
  };

  if (isLoading && roles.length === 0) {
    return (
      <Paper elevation={0} sx={paperSx}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <Paper elevation={0} sx={paperSx}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Role Name</TableCell>
                <TableCell align="right" sx={headerCellSx}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ ...bodyCellSx, borderBottom: 0 }}>
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      No roles yet. Add a role to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                roles.map((role) => (
                  <TableRow
                    key={role.id}
                    hover
                    sx={{
                      '&:last-child td': { borderBottom: 0 },
                    }}
                  >
                    <TableCell sx={{ ...bodyCellSx, color: 'text.primary', fontWeight: 500 }}>{role.name}</TableCell>
                    <TableCell align="right" sx={{ ...bodyCellSx, verticalAlign: 'middle' }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => openModal('view', role)} aria-label="View">
                            <VisibilityOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openModal('edit', role)} aria-label="Edit">
                            <EditOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedRole(role);
                              setDeleteOpen(true);
                            }}
                            aria-label="Delete"
                          >
                            <DeleteOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalElements}
          page={searchQuery.page ?? 0}
          onPageChange={handleChangePage}
          rowsPerPage={searchQuery.size ?? 10}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          sx={{ borderTop: 1, borderColor: 'divider' }}
        />
      </Paper>

      {modalMode && (
        <RoleFormModal open={Boolean(modalMode)} onClose={closeModal} mode={modalMode} roleData={selectedRole} />
      )}

      <RoleDeleteDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        roleData={selectedRole}
        isDeleting={isDeleting}
        onSubmit={() => selectedRole && deleteRole(selectedRole.id)}
      />
    </>
  );
}
